import AuthActionType from './auth.types';
import type { AuthAction, AuthLoginSuccessPayload } from './auth.action';
import type { Role } from '../../data/tisms-api';
import type { AuthUser } from '../../api/httpTismsApi';
import { setAccessToken } from '../_common/tokenHolder';

export interface AuthState {
  authStatus: 'Success' | 'Failure' | null;
  authError: string | null;
  /** Server error code, so a 429 wait can read differently from a bad password. */
  authErrorCode: string | null;
  role: Role | null;
  userName: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  /** Server says: set a new password before anything else works. */
  mustChangePassword: boolean;
}

const INITIAL_STATE: AuthState = {
  authStatus: null,
  authError: null,
  authErrorCode: null,
  role: null,
  userName: null,
  accessToken: null,
  refreshToken: null,
  user: null,
  mustChangePassword: false,
};

/* The signed-in person's name comes from the server. It used to be looked up
   from a fixed per-role map, so every Super Admin was displayed as "Santhosh
   Kumar V" whoever had actually signed in. */

export default (state: AuthState = INITIAL_STATE, action: AuthAction): AuthState => {
  switch (action.type) {
    case AuthActionType.AUTH_LOGIN_SUCCESS: {
      const { role, accessToken, refreshToken, user } = action.payload as AuthLoginSuccessPayload;
      setAccessToken(accessToken);
      return {
        ...state,
        role,
        user,
        accessToken,
        refreshToken,
        userName: user?.name ?? role.name,
        authStatus: 'Success',
        authError: null,
        authErrorCode: null,
        mustChangePassword: (action.payload as AuthLoginSuccessPayload).mustChangePassword === true,
      };
    }
    case AuthActionType.AUTH_LOGIN_FAILURE: {
      const payload = action.payload as { message?: string; code?: string } | undefined;
      return {
        ...state,
        authStatus: 'Failure',
        authError: payload?.message ?? 'Login failed',
        authErrorCode: payload?.code ?? null,
      };
    }
    case AuthActionType.AUTH_TOKENS_REFRESHED: {
      const t = (action as ReturnType<typeof import('./auth.action').authTokensRefreshed>).payload;
      return { ...state, accessToken: t.accessToken, refreshToken: t.refreshToken, mustChangePassword: t.mustChangePassword };
    }
    case AuthActionType.AUTH_LOGIN_CHANGED:
      return { ...state, authStatus: null, authError: null };
    case AuthActionType.AUTH_LOGIN_RESET:
    case AuthActionType.AUTH_LOGOUT:
      setAccessToken(null);
      return INITIAL_STATE;
    default:
      return state;
  }
};
